import React from 'react';
import {
  Box, Typography, Button, TextField, FormControl,
  Chip, IconButton, List, ListItem, ListItemText,
  Switch, Paper, Collapse,
  FormControlLabel, Divider,
  Checkbox, FormGroup, MenuItem,
} from '@mui/material';
import {
  EmojiEmotions as EmojiIcon,
  Lock as LockIcon, AccessTime as TimeIcon,
  Group as GroupIcon, AdminPanelSettings as ApprovalIcon,
  ListAlt as PresetIcon, Close as CloseIcon,
} from '@mui/icons-material';
import { LocalizationProvider, DesktopTimePicker, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/fi';
import { motion, AnimatePresence } from 'framer-motion';
import NumberSpinner from '../shared/NumberSpinner';
import { DAYS, TASK_ICONS, TASK_PRESETS } from '../../utils/taskFormConstants';

const TaskFormContent = ({
  formData,
  setFormData,
  users,
  editingTask,
  isBonusMode,
  setIsBonusMode,
  iconPickerOpen,
  setIconPickerOpen,
  presetDrawerOpen,
  setPresetDrawerOpen,
  datePickerOpen,
  setDatePickerOpen,
  isDark,
}) => {
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      recurrence_days: prev.recurrence_days.includes(day)
        ? prev.recurrence_days.filter(d => d !== day)
        : [...prev.recurrence_days, day].sort()
    }));
  };

  return (
    <>
      {/* Bonus toggle at top */}
      {!editingTask && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FormControlLabel
            control={<Switch checked={isBonusMode} onChange={(e) => setIsBonusMode(e.target.checked)} size="small" />}
            label={<Typography variant="body2" fontWeight={600}>Bonustehtävä</Typography>}
          />
        </Box>
      )}

      {/* Icon centered at top */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 2 }}>
        <Box
          onClick={() => setIconPickerOpen(p => !p)}
          sx={{
            width: 56, height: 56, borderRadius: '14px', border: '1px solid',
            borderColor: 'divider', display: 'flex', alignItems: 'center',
            justifyContent: 'center', cursor: 'pointer', fontSize: '1.6rem',
            bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {formData.icon || <EmojiIcon sx={{ fontSize: 28, color: 'text.secondary' }} />}
        </Box>
      </Box>
      {iconPickerOpen && (
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <TextField
            size="small"
            placeholder="Oma"
            value=""
            onChange={e => {
              const val = e.target.value.trim();
              if (val) { setFormData(p => ({ ...p, icon: val })); setIconPickerOpen(false); }
            }}
            sx={{ width: 72, mr: 1, '& input': { textAlign: 'center', fontSize: '1.3rem', p: '4px 8px' } }}
          />
          {TASK_ICONS.map(emoji => (
            <Box
              key={emoji}
              onClick={() => { setFormData(p => ({ ...p, icon: emoji })); setIconPickerOpen(false); }}
              sx={{
                width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.3rem', cursor: 'pointer', borderRadius: 1,
                bgcolor: formData.icon === emoji ? 'primary.light' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {emoji}
            </Box>
          ))}
        </Box>
      )}

      {/* Title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Tehtävän nimi"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          required
          sx={{ '& .MuiInputBase-input': { fontSize: '0.9rem' } }}
        />
        {!editingTask && (
          <Button
            size="small"
            variant="text"
            startIcon={<PresetIcon sx={{ fontSize: '13px !important' }} />}
            onClick={() => setPresetDrawerOpen(true)}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap', color: 'primary.main', fontWeight: 500, fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
          >
            Esiasetukset
          </Button>
        )}
      </Box>

      {/* Description */}
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder="Muistiinpanot"
        name="description"
        value={formData.description}
        onChange={handleInputChange}
        sx={{ mb: 2, '& .MuiInputBase-input': { fontSize: '0.9rem' } }}
      />

      {/* Points */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          Ansaittavissa olevat pisteet (max {isBonusMode ? 20 : 10})
        </Typography>
        <NumberSpinner
          value={formData.reward_points}
          onValueChange={(val) => setFormData(prev => ({ ...prev, reward_points: val }))}
          min={1}
          max={isBonusMode ? 20 : 10}
          size="small"
        />
      </Box>

      {/* Kenelle? (user checkboxes) */}
      <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <GroupIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" fontWeight={600}>Kenelle?</Typography>
          <Collapse in={formData.assigned_to.length > 1}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: 3, px: 1.5, py: 0.4, mt: 0.5 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'background.default' }}>?</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">Luodaan {formData.assigned_to.length} käyttäjälle erikseen</Typography>
            </Box>
          </Collapse>
        </Box>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.assigned_to.length === 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData(prev => ({ ...prev, assigned_to: [] }));
                  }
                }}
              />
            }
            label="Kaikki"
          />
          {users.map(u => (
            <FormControlLabel
              key={u.id}
              control={
                <Checkbox
                  checked={formData.assigned_to.includes(u.id)}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      assigned_to: e.target.checked
                        ? [...prev.assigned_to, u.id]
                        : prev.assigned_to.filter(id => id !== u.id)
                    }));
                  }}
                />
              }
              label={u.name}
            />
          ))}
        </FormGroup>
      </FormControl>

      {/* Scheduling card */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: 'divider' }}>
        {/* Start date */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="body2" fontWeight={900}>Aloituspäivä</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fi" localeText={{ cancelButtonLabel: '✕' }}>
            <DatePicker
              open={datePickerOpen}
              onOpen={() => setDatePickerOpen(true)}
              onClose={() => setDatePickerOpen(false)}
              value={formData.scheduled_date ? dayjs(formData.scheduled_date) : dayjs()}
              onChange={(v) => setFormData(prev => ({ ...prev, scheduled_date: v ? v.format('YYYY-MM-DD') : '' }))}
              slotProps={{
                textField: {
                  variant: 'outlined', size: 'small',
                  sx: { width: 155, '& .MuiInputBase-input': { cursor: 'pointer', fontSize: '0.85rem' } },
                  inputProps: { readOnly: true },
                  onClick: () => setDatePickerOpen(true),
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Repeat */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" fontWeight={600}>Toista</Typography>
          <TextField
            select
            size="small"
            value={formData.recurrence_type === 'event' ? 'once' : formData.recurrence_type}
            onChange={(e) => setFormData(prev => ({ ...prev, recurrence_type: e.target.value }))}
            sx={{ width: 165, '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
          >
            <MenuItem value="daily">Joka päivä</MenuItem>
            <MenuItem value="once">Kerran</MenuItem>
            <MenuItem value="custom">Tiettyinä päivinä</MenuItem>
          </TextField>
        </Box>

        {/* Custom day chips */}
        <Collapse in={formData.recurrence_type === 'custom'}>
          <Box sx={{ mt: 1, mb: 1 }}>
            <Box sx={{ display: 'flex', gap: 0.5, width: '100%' }}>
              {DAYS.map(day => (
                <Box
                  key={day.value}
                  onClick={() => handleDayToggle(day.value)}
                  sx={{
                    flex: 1, py: 0.75, borderRadius: '8px', textAlign: 'center',
                    fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer',
                    bgcolor: formData.recurrence_days.includes(day.value) ? 'primary.main' : 'action.hover',
                    color: formData.recurrence_days.includes(day.value) ? 'primary.contrastText' : 'text.secondary',
                    transition: 'all 0.15s',
                  }}
                >
                  {day.label}
                </Box>
              ))}
            </Box>

            {/* Interval + end type */}
            <Box sx={{ mt: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Typography variant="body2">Joka</Typography>
                <NumberSpinner
                  value={formData.recurrence_interval}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, recurrence_interval: Math.max(1, val || 1) }))}
                  min={1} max={12} size="small"
                  suffix="viikko"
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" fontWeight={600}>Päättyy</Typography>
                <TextField
                  select
                  size="small"
                  value={formData.recurrence_end_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, recurrence_end_type: e.target.value }))}
                  sx={{ width: 165, '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                >
                  <MenuItem value="never">Ei koskaan</MenuItem>
                  <MenuItem value="on_date">Päivämäärällä</MenuItem>
                  <MenuItem value="after_count">Suoritusten jälkeen</MenuItem>
                </TextField>
              </Box>
              <Collapse in={formData.recurrence_end_type === 'on_date'}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fi" localeText={{ cancelButtonLabel: '✕' }}>
                    <DatePicker
                      value={formData.recurrence_end_date ? dayjs(formData.recurrence_end_date) : null}
                      onChange={(v) => setFormData(prev => ({ ...prev, recurrence_end_date: v ? v.format('YYYY-MM-DD') : '' }))}
                      slotProps={{
                        textField: {
                          variant: 'outlined', size: 'small',
                          sx: { width: 155, '& .MuiInputBase-input': { cursor: 'pointer', fontSize: '0.85rem' } },
                          inputProps: { readOnly: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </Collapse>
              <Collapse in={formData.recurrence_end_type === 'after_count'}>
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NumberSpinner
                    value={parseInt(formData.recurrence_end_count) || 1}
                    onValueChange={(val) => setFormData(prev => ({ ...prev, recurrence_end_count: val }))}
                    min={1} max={999} size="small"
                  />
                  <Typography variant="body2" color="text.secondary">toistokertaa</Typography>
                </Box>
              </Collapse>
            </Box>
          </Box>
        </Collapse>
      </Paper>

      {/* Cooldown */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, mt: 1.5 }}>
        <NumberSpinner
          label="Jäähdytys"
          value={formData.cooldown_days}
          onValueChange={(val) => setFormData(prev => ({ ...prev, cooldown_days: val }))}
          min={0}
          max={7}
          size="small"
          suffix="pv"
        />
      </Box>
      <Collapse in={formData.cooldown_days > 0}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: 3, px: 1.5, py: 0.5, mb: 1.5 }}>
          <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'background.default' }}>?</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">Suorituksen jälkeen piilossa {formData.cooldown_days} päivää</Typography>
        </Box>
      </Collapse>

      {isBonusMode && (
        <Box sx={{ mb: 2 }}>
          <NumberSpinner
            label="Maksimimäärä (0 = rajaton)"
            value={formData.max_claims ? parseInt(formData.max_claims) : 0}
            onValueChange={(val) => setFormData(prev => ({ ...prev, max_claims: val === 0 ? '' : val }))}
            min={0}
            max={99}
            size="small"
          />
        </Box>
      )}

      {/* Badges row: Lukittu + Vaatii hyväksynnän */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5, mt: 1.5 }}>
        <Chip
          icon={<LockIcon />}
          label="Lukittu"
          onClick={() => setFormData(p => ({ ...p, is_locked: !p.is_locked }))}
          color={formData.is_locked ? 'primary' : 'default'}
          variant={formData.is_locked ? 'filled' : 'outlined'}
          sx={{ cursor: 'pointer' }}
        />
        <Chip
          icon={<ApprovalIcon />}
          label="Vaatii hyväksynnän"
          onClick={() => setFormData(p => ({ ...p, requires_approval: !p.requires_approval }))}
          color={formData.requires_approval ? 'primary' : 'default'}
          variant={formData.requires_approval ? 'filled' : 'outlined'}
          sx={{ cursor: 'pointer' }}
        />
      </Box>
      <Collapse in={formData.is_locked}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: 3, px: 1.5, py: 0.5, mb: 1 }}>
          <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'background.default' }}>?</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">Siirtyy seuraavalle päivälle jos jää tekemättä</Typography>
        </Box>
      </Collapse>

      {/* Time window */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, mt: 1, borderRadius: 2, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <TimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="body2" fontWeight={600}>Aikaikkuna</Typography>
        </Box>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fi" localeText={{ cancelButtonLabel: '✕' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <DesktopTimePicker
              label="Alkaa"
              value={formData.time_start ? dayjs(`2000-01-01T${formData.time_start}`) : null}
              onOpen={() => {
                if (!formData.time_start) setFormData(p => ({ ...p, time_start: '08:00' }));
              }}
              onChange={(newValue) => {
                setFormData(prev => ({
                  ...prev,
                  time_start: newValue ? newValue.format('HH:mm') : ''
                }));
              }}
              slotProps={{ textField: { variant: 'outlined', size: 'small', sx: { flex: 1 } } }}
            />
            <DesktopTimePicker
              label="Päättyy"
              value={formData.time_end ? dayjs(`2000-01-01T${formData.time_end}`) : null}
              onOpen={() => {
                if (!formData.time_end) setFormData(p => ({ ...p, time_end: '09:00' }));
              }}
              onChange={(newValue) => {
                setFormData(prev => ({
                  ...prev,
                  time_end: newValue ? newValue.format('HH:mm') : ''
                }));
              }}
              slotProps={{ textField: { variant: 'outlined', size: 'small', sx: { flex: 1 } } }}
            />
          </Box>
        </LocalizationProvider>
      </Paper>

      {/* Active toggle */}
      <FormControlLabel
        control={<Switch checked={formData.is_active} onChange={handleInputChange} name="is_active" />}
        label="Tehtävä aktiivinen"
        sx={{ mb: 1 }}
      />

      {/* Preset task picker */}
      <AnimatePresence>
        {presetDrawerOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setPresetDrawerOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1500 }}
            />
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
              style={{
                position: 'fixed', top: '50%', left: '50%', marginLeft: -150, marginTop: '-30vh',
                width: 300, maxHeight: '60vh',
                zIndex: 1501, borderRadius: 16, overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
              }}
            >
              <Box sx={{
                backdropFilter: 'blur(20px)',
                bgcolor: isDark ? 'rgba(30,30,50,0.92)' : 'rgba(255,255,255,0.92)',
                border: '1px solid', borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                borderRadius: '16px', display: 'flex', flexDirection: 'column', maxHeight: '60vh',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" fontWeight={700}>Valitse esiasetettu tehtävä</Typography>
                  <IconButton size="small" onClick={() => setPresetDrawerOpen(false)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <List sx={{ p: 1, overflowY: 'auto', flex: 1 }}>
                  {TASK_PRESETS.map((preset) => (
                    <ListItem
                      key={preset.title}
                      button
                      onClick={() => {
                        setFormData(p => ({ ...p, title: preset.title, icon: preset.icon }));
                        setPresetDrawerOpen(false);
                      }}
                      sx={{
                        borderRadius: 2, mb: 0.5, py: 0.75,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5, fontSize: '1.1rem' }}>
                        {preset.icon}
                      </Box>
                      <ListItemText primary={preset.title} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default TaskFormContent;
